import { create } from "zustand";
import { DialogStep } from "./useDialogs";
import { MapNPCUnlockMode, type MapData, type NPC } from "../types/MapData.types";
import type { WorldConfig, WorldMapConfig } from "../types/WorldCfg.types";
import { useDevSettings } from "./useDevSettings";
import { api } from "../utils/api";
import type { GameProgress } from "../types/UserDto";
import { LoginState, useAuth } from "./useAuth";
import {
  findNPCProgress,
  updateNPCProgress,
  loadGameData,
  createInitialProgress,
  syncProgressWithMapConfigs,
  unlockNextNPC,
  checkMapUnlock,
  addDependencyFreeMaps,
} from "../utils/levelProgressHelpers";
import { INITIAL_UNLOCKED_MAPS } from "../config/game";

interface GameData {
  worldConfig: WorldConfig;
  mapsData: { [mapId: string]: MapData };
}

interface LevelProgressStore {
  // State
  progress: GameProgress[];
  unlockedMaps: Set<string>;
  gameData: GameData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  isProgressLoaded: boolean;

  // Actions
  initializeGame: () => Promise<void>;
  completeNPC: (mapId: string, npcId: string) => Promise<void>;
  updateNPCDialog: (mapId: string, npcId: string, step: DialogStep, index: number, sectionIndex?: number) => Promise<void>;
  resetProgress: () => Promise<void>;
  syncProgress: () => Promise<void>;
  loadProgressFromServer: () => Promise<void>;
  saveProgressToServer: () => Promise<void>;

  // Selectors
  isMapUnlocked: (mapId: string) => boolean;
  isNPCUnlocked: (mapId: string, npcId: string) => boolean;
  isNPCCompleted: (mapId: string, npcId: string) => boolean;
  getNPCProgress: (mapId: string, npcId: string) => GameProgress | null;
  getWorldConfig: () => WorldConfig | null;
  getMapData: () => { [mapId: string]: MapData };
  getMaps: () => WorldMapConfig[];
  isDataLoaded: () => boolean;
  checkIfNPCUnlocksNewMap: (mapId: string, npcId: string) => string | null;
}


// (helpers moved to LevelProgressService)

const LOCAL_STORAGE_PROGRESS_KEY = 'beeard-graph-surfer-progress';
const LOCAL_STORAGE_UNLOCKED_MAPS_KEY = 'beeard-graph-surfer-unlocked-maps';

// Helper function to check if user is authenticated and we should use server storage
const isUserAuthenticated = (): boolean => {
  const authState = useAuth.getState();
  const devSettings = useDevSettings.getState();

  // If useLocalStorage is enabled, always return false to skip server authentication
  if (devSettings.useLocalStorage) {
    return false;
  }

  return authState.loginState === LoginState.Authentificated;
};

// Helper function to check if localStorage mode is enabled
const isLocalStorageMode = (): boolean => {
  return useDevSettings.getState().useLocalStorage;
};

// Helper function to save progress to localStorage
const saveProgressToLocalStorage = (progress: GameProgress[], unlockedMaps: Set<string>): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_PROGRESS_KEY, JSON.stringify(progress));
    localStorage.setItem(LOCAL_STORAGE_UNLOCKED_MAPS_KEY, JSON.stringify(Array.from(unlockedMaps)));
    console.log("✅ Progress saved to localStorage");
  } catch (error) {
    console.error("❌ Failed to save progress to localStorage:", error);
  }
};

// Helper function to load progress from localStorage
const loadProgressFromLocalStorage = (): { progress: GameProgress[], unlockedMaps: Set<string> } | null => {
  try {
    const progressStr = localStorage.getItem(LOCAL_STORAGE_PROGRESS_KEY);
    const unlockedMapsStr = localStorage.getItem(LOCAL_STORAGE_UNLOCKED_MAPS_KEY);

    if (!progressStr || !unlockedMapsStr) {
      return null;
    }

    const progress = JSON.parse(progressStr) as GameProgress[];
    const unlockedMaps = new Set<string>(JSON.parse(unlockedMapsStr) as string[]);

    console.log("✅ Progress loaded from localStorage");
    return { progress, unlockedMaps };
  } catch (error) {
    console.error("❌ Failed to load progress from localStorage:", error);
    return null;
  }
};

export const useLevelProgress = create<LevelProgressStore>()((set, get) => ({
  // Initial state
  progress: [],
  unlockedMaps: new Set<string>(INITIAL_UNLOCKED_MAPS),
  gameData: null,
  isLoading: false,
  isSaving: false,
  error: null,
  isProgressLoaded: false,

  // Game initialization
  initializeGame: async () => {
    const state = get();
    const allNpcIndependent = useDevSettings.getState().allNpcIndependent;
    const useLocalStorageMode = isLocalStorageMode();

    set({ isLoading: true, error: null });
    console.log("🔄 Initializing game", 'isUserAuthenticated:', isUserAuthenticated(), 'useLocalStorage:', useLocalStorageMode);

    try {
      const gameData = await loadGameData();

      let newProgress = state.progress;
      let newUnlockedMaps = state.unlockedMaps;

      if (useLocalStorageMode) {
        const localData = loadProgressFromLocalStorage();
        if (localData) {
          newProgress = localData.progress;
          newUnlockedMaps = localData.unlockedMaps;
          console.log("✅ Loaded progress from localStorage");
          set({ isProgressLoaded: true });
        }
      }
      else if (isUserAuthenticated()) {
        try {
          const serverProgress = await api.getProgress();
          newProgress = serverProgress.gameProgress;
          newUnlockedMaps = new Set(serverProgress.unlockedMaps);
          console.log("✅ Loaded progress from server");
          set({ isProgressLoaded: true });
        } catch (error) {
          console.warn("⚠️ Failed to load progress from server, using local state:", error);
        }
      }

      // Auto-unlock maps that have no NPC dependencies (no NPC unlocks them)
      newUnlockedMaps = addDependencyFreeMaps(newUnlockedMaps, gameData);

      if (newProgress.length === 0) {
        console.log("🆕 Creating initial progress");
        newProgress = createInitialProgress(gameData, newUnlockedMaps, allNpcIndependent);
      } else {
        console.log("🔄 Syncing existing progress with current map configs");
        newProgress = syncProgressWithMapConfigs(newProgress, gameData, newUnlockedMaps, allNpcIndependent);
      }

      set({
        gameData,
        progress: newProgress,
        unlockedMaps: newUnlockedMaps,
        isLoading: false,
      });

      if (useLocalStorageMode) {
        saveProgressToLocalStorage(newProgress, newUnlockedMaps);
      } else if (isUserAuthenticated()) {
        await get().saveProgressToServer();
      }

      console.log("✅ Game initialized successfully");
    } catch (error) {
      console.error("❌ Failed to initialize game:", error);
      set({ isLoading: false, error: error instanceof Error ? error.message : "Unknown error" });
    }
  },

  // NPC completion
  completeNPC: async (mapId: string, npcId: string) => {
    const state = get();
    if (!state.gameData) return;

    console.log(`✅ Completing NPC ${npcId} on map ${mapId}`);

    // 1. Mark NPC as completed
    let newProgress = updateNPCProgress(state.progress, mapId, npcId, { isCompleted: true });

    // 2. Unlock next NPC on current map (only in sequential mode)
    const mapData = state.gameData.mapsData[mapId];

    if (mapData.npcUnlockMode === MapNPCUnlockMode.SEQUENTIAL) {
      newProgress = unlockNextNPC(newProgress, mapId, npcId, mapData);
    }
    // In independent mode, all NPCs are already unlocked when map is unlocked

    // 3. Check if new maps can be unlocked
    const result = checkMapUnlock(newProgress, state.unlockedMaps, state.gameData, mapId, npcId);

    set({
      progress: result.progress,
      unlockedMaps: result.unlockedMaps,
    });

    const useLocalStorageMode = isLocalStorageMode();

    if (useLocalStorageMode) {
      saveProgressToLocalStorage(result.progress, result.unlockedMaps);
    } else if (isUserAuthenticated()) {
      await get().saveProgressToServer();
    }
  },

  // NPC dialog update
  updateNPCDialog: async (mapId: string, npcId: string, step: DialogStep, index: number, sectionIndex?: number) => {
    let updatedProgress: GameProgress[] = [];

    set((state) => {
      updatedProgress = updateNPCProgress(state.progress, mapId, npcId, {
        dialogStep: step,
        // Update the appropriate index based on the step type
        ...(step === DialogStep.QUEST ? { questIndex: index } : { factIndex: index }),
        // Update section index if provided
        ...(sectionIndex !== undefined ? { sectionIndex } : {}),
      });

      return { progress: updatedProgress };
    });

    const useLocalStorageMode = isLocalStorageMode();

    if (useLocalStorageMode) {
      saveProgressToLocalStorage(updatedProgress, get().unlockedMaps);
    } else if (isUserAuthenticated()) {
      await get().saveProgressToServer();
    }
  },

  // Reset progress
  resetProgress: async () => {
    console.log("🔄 Resetting progress");

    const currentGameData = get().gameData;

    // Start with initially unlocked maps; if we have game data, also add dependency-free maps
    const baseUnlocked = new Set<string>(INITIAL_UNLOCKED_MAPS);
    const updatedUnlocked = currentGameData ? addDependencyFreeMaps(baseUnlocked, currentGameData) : baseUnlocked;

    const initialProgress = currentGameData ?
      createInitialProgress(currentGameData, updatedUnlocked, useDevSettings.getState().allNpcIndependent) : [];

    set({
      progress: initialProgress,
      unlockedMaps: updatedUnlocked,
      error: null,
    });

    const useLocalStorageMode = isLocalStorageMode();

    if (useLocalStorageMode) {
      saveProgressToLocalStorage(initialProgress, updatedUnlocked);
    } else if (isUserAuthenticated()) {
      await get().saveProgressToServer();
    }
  },

  // Sync progress with current dev settings
  syncProgress: async () => {
    const state = get();
    if (!state.gameData) return;

    const allNpcIndependent = useDevSettings.getState().allNpcIndependent;
    const useLocalStorageMode = isLocalStorageMode();

    console.log("🔄 Syncing progress with allNpcIndependent:", allNpcIndependent, "useLocalStorage:", useLocalStorageMode);

    const newProgress = syncProgressWithMapConfigs(
      state.progress,
      state.gameData,
      state.unlockedMaps,
      allNpcIndependent
    );

    set({ progress: newProgress });

    if (useLocalStorageMode) {
      saveProgressToLocalStorage(newProgress, state.unlockedMaps);
    } else if (isUserAuthenticated()) {
      await get().saveProgressToServer();
    }
  },

  // Load progress from server
  loadProgressFromServer: async () => {
    if (!isUserAuthenticated()) {
      console.warn("⚠️ User not authenticated, cannot load progress from server");
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const serverProgress = await api.getProgress();
      const currentGameData = get().gameData;
      const serverUnlocked = new Set(serverProgress.unlockedMaps);
      const augmentedUnlocked = currentGameData ? addDependencyFreeMaps(serverUnlocked, currentGameData) : serverUnlocked;
      set({
        progress: serverProgress.gameProgress,
        unlockedMaps: augmentedUnlocked,
        isLoading: false,
      });
      console.log("✅ Progress loaded from server");
    } catch (error) {
      console.error("❌ Failed to load progress from server:", error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to load progress"
      });
    }
  },

  // Save progress to server
  saveProgressToServer: async () => {
    if (!isUserAuthenticated()) {
      console.warn("⚠️ User not authenticated, cannot save progress to server");
      return;
    }

    const state = get();
    set({ isSaving: true, error: null });

    try {
      await api.saveProgress({
        gameProgress: state.progress,
        unlockedMaps: Array.from(state.unlockedMaps),
      });
      set({ isSaving: false });
      console.log("✅ Progress saved to server");
    } catch (error) {
      console.error("❌ Failed to save progress to server:", error);
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : "Failed to save progress"
      });
    }
  },

  // Selectors
  isMapUnlocked: (mapId: string) => {
    return get().unlockedMaps.has(mapId);
  },

  isNPCUnlocked: (mapId: string, npcId: string) => {
    const npcProgress = findNPCProgress(get().progress, mapId, npcId);
    return npcProgress?.isUnlocked || false;
  },

  isNPCCompleted: (mapId: string, npcId: string) => {
    const npcProgress = findNPCProgress(get().progress, mapId, npcId);
    return npcProgress?.isCompleted || false;
  },

  getNPCProgress: (mapId: string, npcId: string) => {
    return findNPCProgress(get().progress, mapId, npcId);
  },

  getWorldConfig: () => get().gameData?.worldConfig || null,
  getMapData: (): { [mapId: string]: MapData } => get().gameData?.mapsData || {},
  getMaps: (): WorldMapConfig[] => get().gameData?.worldConfig?.maps || [],
  isDataLoaded: () => !!get().gameData,

  // Check if completing this NPC will unlock a new map
  checkIfNPCUnlocksNewMap: (mapId: string, npcId: string) => {
    const state = get();
    if (!state.gameData) return null;

    // Find the NPC
    const mapData = state.gameData.mapsData[mapId];
    const npc = mapData?.npcs.find(n => n.id === npcId);

    if (!npc || !npc.unlocksMapId) return null;

    const targetMapId = npc.unlocksMapId;

    // Check if target map is already unlocked
    if (state.unlockedMaps.has(targetMapId)) return null;

    // Find all NPCs that unlock this target map
    const unlockingNPCs: Array<{ npcId: string, sourceMapId: string }> = [];

    Object.entries(state.gameData.mapsData).forEach(([sourceMapId, sourceMapInfo]) => {
      sourceMapInfo.npcs.forEach((sourceNpc: NPC) => {
        if (sourceNpc.unlocksMapId === targetMapId) {
          unlockingNPCs.push({ npcId: sourceNpc.id, sourceMapId });
        }
      });
    });

    // With new logic, any single NPC completion unlocks the map
    // So if this NPC unlocks a map, it will definitely unlock it
    return targetMapId;
  },
}));